import { cn } from '@utils/cn';
import React, { createElement, forwardRef, Ref } from 'react';

import type { TypographyColorType } from './Heading';
import styles from './typography.module.css';

export type TextVariantType = 'body' | 'body-sm' | 'quote' | 'caption';
export type TextElementType =
  | 'p'
  | 'span'
  | 'div'
  | 'blockquote'
  | 'figcaption';

export type TextProps = Omit<React.HTMLAttributes<HTMLElement>, 'color'> & {
  variant?: TextVariantType;
  /** Defaults to `secondary` for captions and `primary` for everything else. */
  color?: TypographyColorType;
  as?: TextElementType;
};

const VARIANT_CLASS: Record<TextVariantType, string> = {
  'body': styles.UiText__variant__body,
  'body-sm': styles.UiText__variant__bodySm,
  'quote': styles.UiText__variant__quote,
  'caption': styles.UiText__variant__caption,
};

export const Text = forwardRef(
  (
    { as, children, className, color, variant = 'body', ...props }: TextProps,
    ref: Ref<HTMLElement>,
  ) =>
    createElement(
      as ?? (variant === 'quote' ? 'blockquote' : 'p'),
      {
        ...props,
        ref,
        className: cn(
          styles[
            `UiTypography__color__${color ?? (variant === 'caption' ? 'secondary' : 'primary')}`
          ],
          VARIANT_CLASS[variant],
          className,
        ),
      },
      children,
    ),
);

Text.displayName = 'Text';
